/**
 * Task anchor ↔ task type pairing for the create-family task steps
 * (`create_task`, `create_typed_task`, `generate_task`, `create_subtask`).
 *
 * Anchoring is opt-in per task type: `TaskService` rejects an anchored task
 * whose type declares no `anchorEntityType`, rejects an anchor whose entity
 * type differs from the declared one, and the step handlers reject an anchored
 * type that receives no entity at all. Every one of those is decided by the
 * graph plus the step's config — nothing about it needs a run — yet until this
 * check existed each surfaced only as a failed run. This reports them at
 * design time, from the same inputs, so the editor can outline the node and
 * publish can refuse.
 *
 * The declared anchor is read from `config.taskTypeAnchorEntityType`, which the
 * task-type picker copies from the selected type (and the editor re-syncs from
 * the org catalog on load) — the same key that drives the step's socket
 * generation, so validation and canvas agree.
 *
 * A step whose `taskTypeKey` INPUT is fed is skipped: the type that runs is
 * chosen upstream, so the config type (and its anchor) may not be the one that
 * applies. Only the handler can check that graph.
 */

import { SYSTEM_ENTITY_LABELS, type SystemEntity } from "@erp/shared-types";
import {
  isTaskCreateStepType,
  readDeclaredTaskTypeAnchorEntityType,
  type TaskCreateStepType,
} from "../utils/dynamicSockets/taskCreateInputSockets";
import type { ValidationEdge, ValidationError, ValidationStep } from "./types";

/** Input socket a create-family step reads a run-time task-type override from. */
const TASK_TYPE_KEY_SOCKET_ID = "taskTypeKey";

const STATIC_INPUTS_CONFIG_KEY = "__static_inputs";

const ENTITY_REFERENCE_TYPE_PREFIX = "entity_reference:";

/**
 * One way a step can receive its anchor entity: a structured
 * `{ entityType, entityId }` socket, or a `<type>` + `<id>` scalar pair the
 * handler assembles (`assembleStepEntity`).
 */
type EntitySource =
  | { kind: "structured"; socketId: string }
  | { kind: "scalar_pair"; typeSocketId: string; idSocketId: string };

interface TaskCreateAnchorSockets {
  /**
   * Sockets that exist ONLY to anchor. Feeding any of them is an explicit
   * request to anchor, which is a mistake without an anchored type.
   */
  anchorSocketIds: readonly string[];
  /**
   * Entity sources that satisfy a declared anchor, in the precedence order the
   * handler uses (`resolveRequiredAnchorForTaskType`: explicit anchor first,
   * then the source entity). The first fed one is what the run would anchor.
   */
  candidates: readonly EntitySource[];
}

const ANCHOR_SOCKETS_BY_STEP_TYPE: Record<
  TaskCreateStepType,
  TaskCreateAnchorSockets
> = {
  create_task: {
    anchorSocketIds: ["anchorEntity"],
    candidates: [
      { kind: "structured", socketId: "anchorEntity" },
      { kind: "structured", socketId: "sourceEntity" },
      {
        kind: "scalar_pair",
        typeSocketId: "sourceEntityType",
        idSocketId: "sourceEntityId",
      },
    ],
  },
  create_typed_task: {
    anchorSocketIds: ["anchorEntity"],
    candidates: [
      { kind: "structured", socketId: "anchorEntity" },
      { kind: "structured", socketId: "sourceEntity" },
      {
        kind: "scalar_pair",
        typeSocketId: "sourceEntityType",
        idSocketId: "sourceEntityId",
      },
    ],
  },
  generate_task: {
    anchorSocketIds: ["anchorEntity"],
    candidates: [
      { kind: "structured", socketId: "anchorEntity" },
      { kind: "structured", socketId: "sourceEntity" },
    ],
  },
  create_subtask: {
    anchorSocketIds: ["anchorEntity", "anchorEntityType", "anchorEntityId"],
    candidates: [
      { kind: "structured", socketId: "anchorEntity" },
      {
        kind: "scalar_pair",
        typeSocketId: "anchorEntityType",
        idSocketId: "anchorEntityId",
      },
    ],
  },
};

/**
 * What feeds one step's input sockets, resolved the way
 * `findUnconnectedRequiredSockets` resolves them: an incoming edge, a static
 * value in config, or a `manualOverride` socket expression.
 */
interface SocketFeed {
  isFed(socketId: string): boolean;
  /**
   * The entity type the feed carries when it is knowable without running: an
   * edge from an `entity_reference:<TYPE>` output, or a static value that
   * names the type. `undefined` when fed by something untyped (a generic
   * `entity_reference` output, a `json` output, an expression) or not fed.
   */
  entityType(socketId: string): string | undefined;
}

function readStaticInputs(step: ValidationStep): Record<string, unknown> {
  const raw = step.config?.[STATIC_INPUTS_CONFIG_KEY];
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function hasManualOverride(step: ValidationStep, socketId: string): boolean {
  const socket = step.sockets.inputs.find((input) => input.id === socketId);
  return (
    socket?.manualOverride === true && (socket.expression ?? "").trim() !== ""
  );
}

/** `entity_reference:AP_INVOICE` → `AP_INVOICE`; anything else → undefined. */
function entityTypeOfSocketType(
  socketType: string | undefined,
): string | undefined {
  if (!socketType?.startsWith(ENTITY_REFERENCE_TYPE_PREFIX)) return undefined;
  const entityType = socketType.slice(ENTITY_REFERENCE_TYPE_PREFIX.length);
  return entityType === "" ? undefined : entityType;
}

/**
 * The entity type a static value names: a structured `{ entityType }` value on
 * an entity socket, or the bare string on a `<...>EntityType` scalar socket.
 */
function entityTypeOfStaticValue(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "entityType" in value) {
    const entityType = (value as { entityType?: unknown }).entityType;
    return typeof entityType === "string" ? entityType : undefined;
  }
  return undefined;
}

function buildSocketFeed(
  step: ValidationStep,
  edges: ValidationEdge[],
  stepsById: Map<string, ValidationStep>,
): SocketFeed {
  const incomingBySocket = new Map<string, ValidationEdge>();
  for (const edge of edges) {
    if (edge.targetStepId === step.id) {
      incomingBySocket.set(edge.targetSocketId, edge);
    }
  }
  const staticInputs = readStaticInputs(step);

  return {
    isFed(socketId) {
      return (
        incomingBySocket.has(socketId) ||
        staticInputs[socketId] !== undefined ||
        hasManualOverride(step, socketId)
      );
    },
    entityType(socketId) {
      const edge = incomingBySocket.get(socketId);
      if (edge) {
        const output = stepsById
          .get(edge.sourceStepId)
          ?.sockets.outputs.find((socket) => socket.id === edge.sourceSocketId);
        return entityTypeOfSocketType(output?.type);
      }
      if (staticInputs[socketId] !== undefined) {
        return entityTypeOfStaticValue(staticInputs[socketId]);
      }
      return undefined;
    },
  };
}

function candidateSocketIds(candidate: EntitySource): string[] {
  return candidate.kind === "structured"
    ? [candidate.socketId]
    : [candidate.typeSocketId, candidate.idSocketId];
}

/** A scalar pair counts only when both halves are fed — the handler needs both. */
function isCandidateFed(candidate: EntitySource, feed: SocketFeed): boolean {
  return candidateSocketIds(candidate).every((socketId) =>
    feed.isFed(socketId),
  );
}

function candidateEntityType(
  candidate: EntitySource,
  feed: SocketFeed,
): string | undefined {
  return feed.entityType(
    candidate.kind === "structured"
      ? candidate.socketId
      : candidate.typeSocketId,
  );
}

/**
 * Whether any socket that could satisfy the anchor is already `required` on
 * the step. When it is, the generic unconnected-required-socket check owns the
 * "nothing wired" finding (with its own activation gating), so this check
 * stays out of the way rather than reporting the same socket twice.
 */
function hasRequiredCandidateSocket(
  step: ValidationStep,
  candidates: readonly EntitySource[],
): boolean {
  const candidateIds = new Set(candidates.flatMap(candidateSocketIds));
  return step.sockets.inputs.some(
    (socket) => socket.required && candidateIds.has(socket.id),
  );
}

function readTaskTypeKey(step: ValidationStep): string | undefined {
  const key = step.config?.taskTypeKey;
  return typeof key === "string" && key.trim() !== "" ? key : undefined;
}

function isSystemEntity(value: string): value is SystemEntity {
  return value in SYSTEM_ENTITY_LABELS;
}

function entityLabel(entityType: string): string {
  return isSystemEntity(entityType)
    ? SYSTEM_ENTITY_LABELS[entityType]
    : entityType;
}

function stepLabel(step: ValidationStep): string {
  return step.name ?? step.type;
}

function describeCandidates(candidates: readonly EntitySource[]): string {
  return candidates
    .map((candidate) =>
      candidate.kind === "structured"
        ? `"${candidate.socketId}"`
        : `both "${candidate.typeSocketId}" and "${candidate.idSocketId}"`,
    )
    .join(", or ");
}

function toSocketRefs(
  stepId: string,
  socketIds: readonly string[],
): ValidationError["sockets"] {
  return socketIds.map((socketId) => ({ stepId, socketId }));
}

/** An anchor is wired, but no task type — or an unanchored one — will receive it. */
function anchorWithoutAnchoredTypeError(
  step: ValidationStep,
  fedAnchorSocketIds: string[],
): ValidationError {
  const taskTypeKey = readTaskTypeKey(step);
  const label = stepLabel(step);
  return {
    code: "TASK_ANCHOR_WITHOUT_ANCHORED_TYPE",
    message: taskTypeKey
      ? `"${label}" wires an anchor entity, but task type "${taskTypeKey}" does not declare an anchor entity type — the run would fail. Select a task type anchored to the wired entity, or unwire the anchor.`
      : `"${label}" wires an anchor entity but has no task type — the run would fail. Anchoring needs a task type that declares a matching anchor entity type: select one, or unwire the anchor.`,
    nodeIds: [step.id],
    sockets: toSocketRefs(step.id, fedAnchorSocketIds),
  };
}

/** The type anchors, but nothing on the step supplies the entity to anchor to. */
function anchorEntityMissingError(
  step: ValidationStep,
  declaredAnchor: SystemEntity,
  candidates: readonly EntitySource[],
): ValidationError {
  const taskTypeKey = readTaskTypeKey(step);
  const typeDescription = taskTypeKey
    ? `task type "${taskTypeKey}"`
    : "its task type";
  return {
    code: "TASK_ANCHOR_ENTITY_MISSING",
    message: `"${stepLabel(step)}" uses ${typeDescription}, which anchors every task to a ${entityLabel(declaredAnchor)}, but no entity is wired — the run would fail. Wire ${describeCandidates(candidates)}.`,
    nodeIds: [step.id],
    sockets: toSocketRefs(
      step.id,
      candidates.flatMap((candidate) => candidateSocketIds(candidate)),
    ),
  };
}

/** The wired entity is of a type the task type does not anchor to. */
function anchorTypeMismatchError(
  step: ValidationStep,
  declaredAnchor: SystemEntity,
  fedEntityType: string,
  candidate: EntitySource,
): ValidationError {
  const taskTypeKey = readTaskTypeKey(step);
  const typeDescription = taskTypeKey
    ? `task type "${taskTypeKey}"`
    : "its task type";
  return {
    code: "TASK_ANCHOR_TYPE_MISMATCH",
    message: `"${stepLabel(step)}" is wired a ${entityLabel(fedEntityType)} as its anchor, but ${typeDescription} anchors to a ${entityLabel(declaredAnchor)} — the run would fail. Wire a ${entityLabel(declaredAnchor)}, or select a task type anchored to a ${entityLabel(fedEntityType)}.`,
    nodeIds: [step.id],
    sockets: toSocketRefs(step.id, candidateSocketIds(candidate)),
  };
}

function checkTaskCreateStep(
  step: ValidationStep,
  sockets: TaskCreateAnchorSockets,
  feed: SocketFeed,
): ValidationError | undefined {
  const declaredAnchor = readDeclaredTaskTypeAnchorEntityType(
    step.config ?? {},
  );

  if (!declaredAnchor) {
    const fedAnchorSocketIds = sockets.anchorSocketIds.filter((socketId) =>
      feed.isFed(socketId),
    );
    return fedAnchorSocketIds.length > 0
      ? anchorWithoutAnchoredTypeError(step, fedAnchorSocketIds)
      : undefined;
  }

  const candidate = sockets.candidates.find((entry) =>
    isCandidateFed(entry, feed),
  );
  if (!candidate) {
    return hasRequiredCandidateSocket(step, sockets.candidates)
      ? undefined
      : anchorEntityMissingError(step, declaredAnchor, sockets.candidates);
  }

  const fedEntityType = candidateEntityType(candidate, feed);
  return fedEntityType !== undefined && fedEntityType !== declaredAnchor
    ? anchorTypeMismatchError(step, declaredAnchor, fedEntityType, candidate)
    : undefined;
}

/**
 * Find create-family task steps whose wired anchor entity and selected task
 * type cannot agree at run time. Always blocking: unlike a half-wired draft,
 * there is no run in which such a step succeeds — only a config change fixes
 * it — and draft saves skip graph validation anyway, so this gates publish
 * without getting in the way of work in progress.
 */
export function findTaskAnchorBindingErrors(
  steps: ValidationStep[],
  edges: ValidationEdge[],
): ValidationError[] {
  const stepsById = new Map(steps.map((step) => [step.id, step]));
  const errors: ValidationError[] = [];

  for (const step of steps) {
    if (!isTaskCreateStepType(step.type)) continue;
    const feed = buildSocketFeed(step, edges, stepsById);
    // The type that runs is picked upstream; the config type may not apply.
    if (feed.isFed(TASK_TYPE_KEY_SOCKET_ID)) continue;

    const error = checkTaskCreateStep(
      step,
      ANCHOR_SOCKETS_BY_STEP_TYPE[step.type],
      feed,
    );
    if (error) errors.push(error);
  }

  return errors;
}
